import React, { useCallback, useMemo } from 'react';
import { Alert, Box, TextField, Typography } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import {
	EntityType,
	PopulatedEntry,
	PropertyDefinition,
	PropertyDefinitionBelongsTo,
	PropertyDefinitionHas,
} from '../types';
import FormTemplate, { hideIdleInputUnderlineSx } from './FormTemplate';
import { RelationSelectField } from './RelationSelectField';
import type { ControlledFormData } from './EditEntryForm';
import type { RelationValue } from './EntryPropertyBelongsToEditDialog';
import {
	entryPropertyGetReadableValue,
	initEditFormData,
	isEntryTimestampPropertyDefinition,
	sanitizeEditFormDataObject,
} from '../common/util';

const isRelationDef = (def: PropertyDefinition): def is PropertyDefinitionBelongsTo | PropertyDefinitionHas =>
	def.type === EntityType.PropertyDefinitionBelongsTo || def.type === EntityType.PropertyDefinitionHas;

export type RepositoryEntryFieldsFormProps = {
	populatedEntry: PopulatedEntry;
	propertyDefinitions: PropertyDefinition[];
	onChange: (entryId: string, values: ControlledFormData) => void;
	getRelationValues: (entry: PopulatedEntry, def: PropertyDefinitionBelongsTo | PropertyDefinitionHas) => RelationValue[];
	onRelationChange: (entryId: string, def: PropertyDefinitionBelongsTo | PropertyDefinitionHas, nextValues: RelationValue[]) => void;
	onCreateRelatedEntryClick: (def: PropertyDefinitionBelongsTo | PropertyDefinitionHas, currentEntryIds: string[]) => void;
};

export function RepositoryEntryFieldsForm({
	populatedEntry,
	propertyDefinitions,
	onChange,
	getRelationValues,
	onRelationChange,
	onCreateRelatedEntryClick,
}: RepositoryEntryFieldsFormProps) {
	const entryId = populatedEntry.id as string;
	const isArchived = Boolean(populatedEntry.isArchived);
	const storedValues = useMemo(() => initEditFormData(propertyDefinitions, populatedEntry.data), [propertyDefinitions, populatedEntry.data]);
	const autoFocusDefId = isArchived
		? null
		: propertyDefinitions.find((def) => !isEntryTimestampPropertyDefinition(def))?.id;

	const handleValueChange = useCallback((slug: string, value: unknown) => {
		const changedDefs = propertyDefinitions.filter((def) => def.slug === slug);
		onChange(entryId, sanitizeEditFormDataObject(changedDefs, { [slug]: value }));
	}, [entryId, propertyDefinitions, onChange]);

	return (
		<Box display="flex" flexDirection="column" sx={hideIdleInputUnderlineSx}>
			{propertyDefinitions.map((def) => {
				if (isRelationDef(def)) {
					return (
						<RelationSelectField
							key={def.id}
							def={def}
							values={getRelationValues(populatedEntry, def)}
							disabled={isArchived}
							autoFocus={def.id === autoFocusDefId}
							onChange={(nextValues) => onRelationChange(entryId, def, nextValues)}
							onCreateNewClick={(currentEntryIds) => onCreateRelatedEntryClick(def, currentEntryIds)}
						/>
					);
				}
				if (isEntryTimestampPropertyDefinition(def)) {
					return (
						<TextField
							key={def.id}
							label={def.label}
							value={entryPropertyGetReadableValue(def, populatedEntry.data[def.slug])}
							variant="filled"
							margin="dense"
							fullWidth
							slotProps={{ input: { readOnly: true } }}
							disabled
						/>
					);
				}
				return (
					// The inputs are uncontrolled and only read their value on mount, so a field is keyed by its stored
					// value to remount when that changes from outside the form (e.g. a timeline drag reschedules the date).
					<FormTemplate
						key={`${def.id}:${JSON.stringify(storedValues[def.slug])}`}
						propertyDefinitions={[def]}
						formData={storedValues}
						onValueChange={handleValueChange}
						readOnly={isArchived}
						autoFocusSlug={def.slug}
						disableAutoFocus={def.id !== autoFocusDefId}
					/>
				);
			})}
			{isArchived && (
				<Box display="flex" justifyContent="center" mt={2}>
					<Alert icon={<LockOutlinedIcon />} severity="info" sx={{ boxShadow: 'none' }} elevation={0}>
						<Typography variant="body1" color="text.secondary">This entry is archived.</Typography>
					</Alert>
				</Box>
			)}
		</Box>
	);
}
